import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Home } from '@components/templates';
import { SITE_URLS } from '@constants';
import { useSEOMetatags } from '@hooks/useSEOMetatags';
import { useData } from '@hooks/useData';
import { useGetHomepageStores } from '@services/useGetHomepageStores';
import { useGetAllCarouselData } from '@services/useGetAllCarouselData';

export function HomePage() {
    const { t } = useTranslation();
    const { allSettings, allCarouselData, homepageStores } = useData();
    const { request: requestAllCarouselData } = useGetAllCarouselData();
    const { request: requestGetHomepageStores } = useGetHomepageStores();

    const mappedCarouselData = (allCarouselData?.data || []).map((item) => {
        let link;

        if (typeof item.link === 'object' && item.link !== null && 'label' in item.link && 'url' in item.link) {
            link = item.link;
        } else {
            link = { label: '', url: '' };
        }

        return {
            theme: item.theme || 'light',
            desktopImg: item.desktopImg,
            mobileImg: item.mobileImg,
            category: item.category || '',
            title: item.title || '',
            subtitle: item.subtitle || '',
            link: {
                label: (link.label as string) || '',
                url: (link.url as string) || '',
            },
            footerNote: item.footerNote || '',
        };
    });

    const translations = {
        title: t('home-component.metatags.title', { marketplace: allSettings?.data?.marketplaceName }),
        description: t('home-component.metatags.description', { marketplace: allSettings?.data?.marketplaceName }),
    };

    const seoImage = allCarouselData?.data?.[0]?.desktopImg;

    useSEOMetatags({
        title: translations.title,
        description: translations.description,
        image: seoImage?.src
            ? {
                  src: seoImage.src || '',
                  type: seoImage.type,
                  width: seoImage.width,
                  height: seoImage.height,
                  alt: allCarouselData?.data?.[0].title || '',
              }
            : undefined,
        url: SITE_URLS.home,
        index: true,
    });

    useEffect(() => {
        !allCarouselData && requestAllCarouselData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allCarouselData]);

    useEffect(() => {
        requestGetHomepageStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const mappedHomepageStores = (homepageStores?.data || [])
        .filter((store) => store?.uuid && store?.name)
        .map((store) => ({
            uuid: store.uuid,
            name: store.name || '',
            logo: {
                src: store.logo?.src || '',
                type: store.logo?.type || '',
                width: store.logo?.width || 0,
                height: store.logo?.height || 0,
                isDefaultLogo: store.logo?.is_default_logo,
            },
            category: store.category?.icon
                ? {
                      name: store.category?.name || '',
                      icon: {
                          src: store.category?.icon?.src || '',
                          type: store.category?.icon?.type || '',
                          width: store.category?.icon?.width || 0,
                          height: store.category?.icon?.height || 0,
                      },
                  }
                : undefined,
            address: store.address || '',
            municipality: store.municipality || '',
            link: SITE_URLS.merchantDetail.replace(':uuid', `${store.uuid}`),
            initialFavorite: store.favorite || false,
        }));

    return <Home carouselItems={mappedCarouselData} services={mappedHomepageStores} />;
}
